import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Lock, Plus, Trash2, Check } from "lucide-react";
import { inr, CHARGE_LABEL, FREQ_LABEL, taxLabel, optionalServicesFor } from "./fq";

const Choice = ({ active, onClick, children, testid, className = "" }) => (
  <button type="button" aria-pressed={active} onClick={onClick} data-testid={testid}
    className={`fq-choice ${active ? "is-active" : ""} ${className}`}>
    {active && <Check className="fq-choice-check w-4 h-4" />}
    {children}
  </button>
);

const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-semibold text-[#3A0A06]">{label}</Label>
    {children}
    {hint && <p className="text-xs opacity-70">{hint}</p>}
  </div>
);

export function StepLocation({ cat, config, set }) {
  const loc = config.location;
  const setLoc = (k, v) => set({ ...config, location: { ...loc, [k]: v } });
  return (
    <div className="space-y-8">
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Proposed city *"><Input data-testid="cfg-city" value={loc.city} onChange={(e) => setLoc("city", e.target.value)} placeholder="e.g. Nashik" /></Field>
        <Field label="State"><Input data-testid="cfg-state" value={loc.state} onChange={(e) => setLoc("state", e.target.value)} placeholder="e.g. Maharashtra" /></Field>
        <Field label="Area (sq ft)"><Input data-testid="cfg-area" type="number" min="0" value={loc.area_sqft} onChange={(e) => setLoc("area_sqft", e.target.value)} /></Field>
        <Field label="Approx. monthly rent (₹), if known"><Input data-testid="cfg-rent" type="number" min="0" value={loc.monthly_rent} onChange={(e) => setLoc("monthly_rent", e.target.value)} /></Field>
      </div>
      <Field label="Property status *">
        <div className="flex flex-wrap gap-2" role="group">
          {["owned", "rented", "searching"].map((p) => (
            <Choice key={p} active={loc.property_status === p} onClick={() => setLoc("property_status", p)} testid={`cfg-property-${p}`} className="fq-pill">
              {p === "searching" ? "Still searching" : p[0].toUpperCase() + p.slice(1)}
            </Choice>
          ))}
        </div>
      </Field>
      <Field label="Centre format *" hint="Reference franchise fees come from the FOCO schedule. Site and format suitability will be assessed.">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {cat.formats.map((f) => (
            <Choice key={f.id} active={config.format_id === f.id} onClick={() => set({ ...config, format_id: f.id })} testid={`cfg-format-${f.id}`}>
              <div className="font-display text-xl text-[#3A0A06]">{f.label}</div>
              <div className="text-sm mt-1">{f.area}</div>
              <div className="text-sm opacity-70">{f.seats}</div>
              <div className="text-xs mt-3 uppercase tracking-wider opacity-60">Reference fee</div>
              <div className="font-semibold">{inr(f.reference_fee)}</div>
            </Choice>
          ))}
        </div>
      </Field>
    </div>
  );
}

const modelTerms = (m) => [
  m.fixed_fee && `${m.fixed_fee.label}: ${inr(m.fixed_fee.amount)} + GST / month + services`,
  m.royalty_bp && `Royalty ${m.royalty_bp / 100}% of ${m.royalty_basis} + services`,
  m.profit_share && `Distributable profit ${m.profit_share.partner_pct}% partner : ${m.profit_share.pb_pct}% Purnabramha`,
].filter(Boolean);

export function StepModel({ cat, config, set }) {
  const model = cat.models.find((m) => m.id === config.model_id);
  const fmt = cat.formats.find((f) => f.id === config.format_id);
  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-3 gap-4">
        {cat.models.map((m) => (
          <Choice key={m.id} active={config.model_id === m.id} onClick={() => set({ ...config, model_id: m.id, acknowledgements: {} })} testid={`cfg-model-${m.id}`}>
            <div className="text-xs uppercase tracking-widest text-[#8A4B0F]">{m.full_name}</div>
            <div className="font-display text-2xl text-[#3A0A06] mt-1">{m.label}</div>
            <p className="text-sm mt-2">{m.summary}</p>
            <ul className="mt-3 space-y-1 text-sm font-semibold text-[#7A0000]">{modelTerms(m).map((t) => <li key={t}>{t}</li>)}</ul>
            {fmt && m.id !== "foco" && <p className="text-xs mt-3 text-[#8A4B0F]">{fmt.label} fee for {m.label} awaits confirmation — quote will be provisional.</p>}
          </Choice>
        ))}
      </div>
      {model && (
        <div className="fq-panel" data-testid="cfg-model-detail">
          <h4 className="font-display text-lg mb-2">How {model.label} works</h4>
          <ul className="list-disc pl-5 space-y-1 text-sm">{model.responsibilities.map((r) => <li key={r}>{r}</li>)}</ul>
          {model.acknowledgements.map((a) => (
            <label key={a.id} className="flex items-start gap-3 mt-4 p-3 bg-white rounded-md border border-[#E3D7C6] cursor-pointer">
              <Checkbox data-testid={`cfg-ack-${a.id}`} checked={!!config.acknowledgements[a.id]}
                onCheckedChange={(v) => set({ ...config, acknowledgements: { ...config.acknowledgements, [a.id]: !!v } })} />
              <span className="text-sm font-medium">I acknowledge: {a.text} *</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

const priceText = (s) => {
  if (s.charge_type === "INCLUDED") return "No separate charge";
  if (s.unit_price != null) return `${inr(s.unit_price)} ${s.charge_type === "ONE_TIME" ? "once" : `/ ${s.unit} / month`}`;
  if (s.charge_type === "SHARED_POOL") return "Shared actual cost";
  if (s.charge_type === "ACTUAL") return "Actual cost";
  if (s.charge_type === "STAFFING_SIX_MONTH") return "1 monthly salary / employee / 6 months";
  return "To be confirmed";
};

const ResponseControl = ({ s, type, value, onChange }) => {
  if (type === "QTY") return (
    <div className="flex items-center gap-2">
      <Label className="text-xs">Quantity</Label>
      <Input data-testid={`cfg-svc-${s.id}-qty`} type="number" min="0" step="1" className="w-24" value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Math.max(0, Math.floor(Number(e.target.value))))} />
    </div>
  );
  const opts = type === "PB_OWN" ? [["PB", "Purnabramha"], ["OWN", "Own team"]] : [["YES", "Yes"], ["NO", "No"]];
  return (
    <div className="flex gap-2" role="radiogroup" aria-label={s.name}>
      {opts.map(([v, l]) => (
        <Choice key={v} active={value === v} onClick={() => onChange(v)} testid={`cfg-svc-${s.id}-${v.toLowerCase()}`} className="fq-pill">{l}</Choice>
      ))}
    </div>
  );
};

export function StepServices({ cat, config, set, showMissing }) {
  const mid = config.model_id;
  const services = cat.services.filter((s) => s.applicability[mid] && s.applicability[mid] !== "NA");
  const setResp = (id, v) => set({ ...config, responses: { ...config.responses, [id]: v } });
  const optionalIds = new Set(optionalServicesFor(cat, mid).map((s) => s.id));
  return (
    <div className="space-y-3">
      {services.map((s) => {
        const disabled = s.enabled === false;
        const req = s.applicability[mid] === "REQUIRED";
        const missing = showMissing && optionalIds.has(s.id) && (config.responses[s.id] === undefined || config.responses[s.id] === "");
        return (
          <div key={s.id} className={`fq-service ${missing ? "is-missing" : ""} ${disabled ? "opacity-60" : ""}`} data-testid={`cfg-svc-${s.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="font-semibold text-[#3A0A06]">{s.name}</h4>
                {req && !disabled && <span className="fq-tag lock"><Lock className="w-3 h-3" /> Mandatory</span>}
                {disabled && <span className="fq-tag">Not offered — pending definition</span>}
              </div>
              <p className="text-sm mt-1 opacity-80">{s.includes}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs opacity-80">
                <span><b>Charge:</b> {CHARGE_LABEL[s.charge_type]}</span>
                <span><b>Price:</b> {priceText(s)}</span>
                <span><b>Frequency:</b> {FREQ_LABEL[s.frequency] || s.frequency}</span>
                <span><b>Tax:</b> {taxLabel(s.tax)}</span>
                <span><b>By:</b> {s.performer}</span>
              </div>
              {s.notes && <p className="text-xs mt-1 text-[#8A4B0F]">{req ? `Locked: required in this model. ` : ""}{s.notes}</p>}
              {missing && <p className="text-xs mt-1 text-red-700 font-semibold">Please choose a response.</p>}
            </div>
            <div className="shrink-0">
              {!disabled && !req && <ResponseControl s={s} type={s.response_type[mid]} value={config.responses[s.id]} onChange={(v) => setResp(s.id, v)} />}
            </div>
            {s.id === "domain_email" && config.responses.domain_email === 0 && <p className="w-full text-xs opacity-80">You can give an alternative contact email when you submit your enquiry.</p>}
            {s.id === "meta_ads" && config.responses.meta_ads === "YES" && (
              <div className="w-full sm:w-72"><Field label="Proposed monthly ad budget (₹, optional)"><Input data-testid="cfg-ads-budget" type="number" min="0" value={config.meta_ads_budget} onChange={(e) => set({ ...config, meta_ads_budget: e.target.value })} /></Field></div>
            )}
          </div>
        );
      })}
      {mid === "fofo" && (
        <div className="fq-panel">
          <Field label="Optional: illustrative monthly Gross Sales (₹)" hint="Only used to illustrate the 6% royalty. Leave blank to show the percentage only.">
            <Input data-testid="cfg-gross-sales" type="number" min="0" className="max-w-xs" value={config.scenario.gross_sales_monthly}
              onChange={(e) => set({ ...config, scenario: { gross_sales_monthly: e.target.value } })} />
          </Field>
        </div>
      )}
    </div>
  );
}

export function StepStaffing({ config, set }) {
  const on = config.responses.staff_sourcing === "YES";
  const rows = config.staffing;
  const upd = (i, k, v) => set({ ...config, staffing: rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)) });
  if (!on) return (
    <div className="fq-panel" data-testid="cfg-staffing-off">
      <p className="text-sm">You chose <b>No</b> for staff sourcing. No staffing fee is added.</p>
      <button type="button" className="fq-link mt-2" data-testid="cfg-staffing-enable" onClick={() => set({ ...config, responses: { ...config.responses, staff_sourcing: "YES" }, staffing: rows.length ? rows : [{ role: "", qty: 1, monthly_salary: "", salary_status: "PROPOSED", start_date: "" }] })}>Switch to Yes and add roles</button>
    </div>
  );
  const total = rows.reduce((a, r) => a + (Number(r.qty) || 0) * (Number(r.monthly_salary) || 0), 0);
  return (
    <div className="space-y-4">
      <p className="text-sm opacity-80">Six-month sourcing fee = sum of (quantity × agreed monthly salary). Salaries are illustrative until agreed. Wages are paid separately every month. No guarantee of retention, tenure or free replacement.</p>
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-2 md:grid-cols-[2fr_80px_1fr_1fr_auto] gap-3 items-end fq-panel" data-testid={`cfg-role-${i}`}>
          <Field label="Role"><Input data-testid={`cfg-role-${i}-name`} value={r.role} onChange={(e) => upd(i, "role", e.target.value)} placeholder="e.g. Chef" /></Field>
          <Field label="Qty"><Input data-testid={`cfg-role-${i}-qty`} type="number" min="1" value={r.qty} onChange={(e) => upd(i, "qty", e.target.value)} /></Field>
          <Field label="Monthly salary (₹)"><Input data-testid={`cfg-role-${i}-salary`} type="number" min="0" value={r.monthly_salary} onChange={(e) => upd(i, "monthly_salary", e.target.value)} /></Field>
          <Field label="Start date"><Input data-testid={`cfg-role-${i}-start`} type="date" value={r.start_date} onChange={(e) => upd(i, "start_date", e.target.value)} /></Field>
          <button type="button" aria-label="Remove role" data-testid={`cfg-role-${i}-remove`} className="p-2 text-red-700" onClick={() => set({ ...config, staffing: rows.filter((_, j) => j !== i) })}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button type="button" className="fq-link inline-flex items-center gap-1" data-testid="cfg-role-add" onClick={() => set({ ...config, staffing: [...rows, { role: "", qty: 1, monthly_salary: "", salary_status: "PROPOSED", start_date: "" }] })}><Plus className="w-4 h-4" /> Add role</button>
      <div className="fq-subtotal"><span>Illustrative sourcing fee per six-month period</span><span className="font-display text-xl" data-testid="cfg-staffing-total">{inr(total * 100)}</span></div>
    </div>
  );
}
